import SafeImage from "@/components/ui/SafeImage";
import VideoPlayerV3 from "@/components/ui/VideoPlayerV3";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import DescriptionIcon from "@/assets/icons/Description.svg";
import { CustomButton } from "@/components/ui/Buttons";
import FacebookIcon from "@/assets/icons/Facebook.svg";
import InstagramIcon from "@/assets/icons/Instagram.svg";
import Sparkle from "@/assets/icons/SparkleWhite.svg";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import AiIcon from "@/assets/icons/AiGenerateIcon.svg";
import ModalVideoAnalysis from "./ModalVideoAnalysis";
import { useGetCompetitorVideosQuery } from "@/redux/feature/product/productApi";
import { usePagination } from "@/hooks/usePagination";

type ModalVideoDetailsProps = {
  video: VideoData;
  addGetVideoScript: any;
  currentTab: "videoDetails" | "generateScript" | "videoAnalysis";
  setCurrentTab: Dispatch<
    SetStateAction<"videoDetails" | "generateScript" | "videoAnalysis">
  >;
  showGenerateScript: boolean;
};

const ModalVideoDetails = ({
  video,
  setCurrentTab,
  addGetVideoScript,
  currentTab,
  showGenerateScript,
}: ModalVideoDetailsProps) => {
  console.log(video);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { brandName, productName, country, productId } = useParams();

  const [expanded, setExpanded] = useState(false);

  const captionText = video?.caption || "";
  const shouldTruncate = captionText.length > 500;

  const visibleText =
    expanded || !shouldTruncate
      ? captionText
      : captionText.slice(0, 500) + "...";

  const competitorPagination = usePagination();

  const handlerGenerateScript = async () => {
    setCurrentTab("generateScript");

    const payload = {
      brand: brandName,
      product: productName,
      product_id: productId,
      country: country,
      video_id: video.id,
      source: video.ad_or_organic,
    };

    try {
      await addGetVideoScript(payload).unwrap();
    } catch (err: any) {
      console.error("Video script generation error:", err);
      setCurrentTab("videoDetails"); // Go back to video details on error
      toast.error(
        err.data?.message || "Failed to generate script. Please try again."
      );
    }
  };

  // competitor videos api
  const [pollingStatus, setPollingStatus] = useState({
    competitor: true,
  });
  const POLLING_INTERVAL = 10000;

  const {
    data: competitorResponse = { data: [], meta: { isProcessing: false } },
    isFetching: isCompetitorFetching,
    isLoading: isCompetitorLoading,
  } = useGetCompetitorVideosQuery(
    {
      productId: productId!,
      page: competitorPagination.page,
      limit: competitorPagination.limit,
    },
    {
      pollingInterval: pollingStatus.competitor ? POLLING_INTERVAL : 0,
    }
  );

  // effect for stop pooling
  useEffect(() => {
    setPollingStatus((prev) => ({
      ...prev,

      competitor: competitorResponse.meta?.isProcessing !== false,
    }));
  }, [competitorResponse.meta]);

  return (
    <div className="flex gap-5">
      {/* Video Player Section */}
      <div className="h-[611px] w-[332px] relative rounded-xl bg-gray-200 flex-shrink-0">
        <VideoPlayerV3
          thumbnailUrl={video.thumbnail_url}
          url={video.video_url as string}
          currentlyPlaying={currentlyPlaying}
          setCurrentlyPlaying={setCurrentlyPlaying}
          className="rounded-xl w-full h-full object-cover"
        />

        {/* Navigation Controls */}
        {/* <div className="bg-white border border-gray-200 px-2 py-0.5 flex items-center gap-1 absolute top-2 left-1.5 z-50 rounded-lg hover:bg-gray-50">
              <ArrowLeftCircle />
              <span className="text-xs font-medium text-gray-700">
                Previous
              </span>
            </div> */}

        {/* <div className="bg-white border border-gray-200 px-2 py-0.5 flex items-center gap-1 absolute top-2 right-1.5 z-50 rounded-lg hover:bg-gray-50">
              <span className="text-xs font-medium text-gray-700">Next</span>
              <span className="rotate-180">
                {" "}
                <ArrowLeftCircle />
              </span>
            </div> */}

        {/* Download Button */}
        {/* <div className="bg-white border border-gray-200 px-4 py-0.5 flex items-center gap-1 absolute bottom-2 left-1.5 z-50 rounded-lg hover:bg-gray-50">
              <DownloadIcon />
              <span className="text-xs font-medium text-gray-700">Video</span>
            </div> */}
      </div>

      {/* Video Details Section */}
      <div className="flex-1 relative h-[611px] flex flex-col">
        {/* <h3 className="text-xl font-medium text-gray-900 mb-2">
              {video.caption || "Video Title"}
            </h3> */}

        <div className="flex items-center gap-1.5 mb-5">
          <div className="size-10 rounded-full bg-gray-300">
            {video.page_profile_picture_url ? (
              <SafeImage
                src={video.page_profile_picture_url}
                alt={`${video.brand} logo`}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-100"
                fallbackComponent={
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex-shrink-0 ring-2 ring-gray-100 flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {video.brand?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                  </div>
                }
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0 ring-2 ring-gray-100" />
            )}
          </div>
          <p className="text-xl font-medium text-gray-900">
            {video.brand || "Amazon"}
          </p>
        </div>

        <div className="flex gap-x-7">
          <button
            onClick={() => setCurrentTab("videoDetails")}
            className={`inline-flex gap-1 items-center pb-2 ${
              currentTab === "videoDetails"
                ? "border-b-[2px] border-gray-900"
                : ""
            }`}
          >
            <DescriptionIcon />
            <p className="font-semibold text-xs">Description</p>
          </button>
          <button
            onClick={() => setCurrentTab("videoAnalysis")}
            className={`inline-flex gap-1 items-center pb-2 ${
              currentTab === "videoAnalysis"
                ? "border-b-[2px] border-gray-900"
                : ""
            }`}
          >
            <AiIcon />
            <p className="font-semibold text-xs">AI Video Analysis</p>
          </button>
        </div>

        {/* Scrollable Content */}

        <div className="flex-1 overflow-y-auto pr-1">
          {currentTab === "videoAnalysis" && (
            <ModalVideoAnalysis
              videoUrl={video.video_url as string}
              thumbnail={video.thumbnail_url as string}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
            />
          )}

          {currentTab === "videoDetails" && (
            <div className="mt-5">
              <div>
                <p className="text-xs font-medium text-gray-900">
                  {visibleText}
                </p>

                {shouldTruncate && (
                  <button
                    onClick={() => setExpanded((prev) => !prev)}
                    className="text-xs font-medium text-blue-600 mt-1"
                  >
                    {expanded ? "See less" : "See more"}
                  </button>
                )}
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex gap-x-1 items-center rounded-[10px]">
                  <p className="text-xs">
                    <span className="text-gray-600">Status:</span>{" "}
                    <span className="text-gray-900">
                      {video.days_since_running} days
                    </span>
                  </p>
                  <div className="text-[#0B8000] bg-[#E2FFE3] py-[2px] px-[8.5px] text-[10px] font-semibold leading-[125%] tracking-[-0.075px]">
                    Still Running
                  </div>
                </div>

                <PlatformsSection />
              </div>
            </div>
          )}
        </div>

        {/* Fixed Button at Bottom */}
        {showGenerateScript && (
          <div className="absolute bottom-0 left-0 right-0 bg-white">
            <CustomButton
              onClick={handlerGenerateScript}
              backgroundColor="#1B1B1B"
              padding="4px"
              borderRadius="8px"
              fontSize="14px"
              fontWeight="500"
              className="flex items-center justify-center gap-x-2.5 leading-5 tracking-[-0.35px] w-full"
              disabled={pollingStatus.competitor}
            >
              <Sparkle /> <span>Generate Similar Scripts</span>
            </CustomButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalVideoDetails;

// Only extracted platform section as requested
const PlatformsSection = () => (
  <div className="flex text-xs">
    <span className="text-gray-600">Platforms:</span>
    <div className="flex gap-2 ml-1">
      <div className="text-gray-900 flex items-center gap-1">
        <FacebookIcon />
        Facebook
      </div>
      <div className="text-gray-900 flex items-center gap-1">
        <InstagramIcon />
        Instagram
      </div>
    </div>
  </div>
);
